/* verqo — Investor Pitch Deck v2 (18 slides · 1920×1080)
   Visual-first rebuild: real logos, animated storytelling, minimal copy.
   Preview route: /pitch-v2 (v1 stays at /pitch until approved).
*/
import { useEffect, useRef, useState, useCallback } from "react";
import {
  Banknote, ShieldCheck, Truck, Zap, Clock, Mail, TrendingDown,
  CreditCard, Wallet, Gauge, Layers, Building2, Users, Rocket,
  Landmark, FileSearch, PhoneCall, Sparkles, AlertTriangle,
  UtensilsCrossed, SprayCan, Shirt, PawPrint, Pill, Baby, Headphones,
} from "lucide-react";
import VerqoLockup from "../components/VerqoLockup";

const LABELS = [
  "Wholesale at the speed of business",
  "Why us",
  "Fragmented by default",
  "The tooling tax",
  "Working capital",
  "Underwriting blind spot",
  "The turning point",
  "The platform",
  "Instant actions",
  "The flow",
  "Banking at the core",
  "Who we serve",
  "Why now",
  "The opportunity",
  "Proof",
  "Revenue engine",
  "Differentiation",
  "What's next",
  "The vision",
  "Use of proceeds",
  "The mission",
  "Thank you",
];
const SLIDES = LABELS.length;

/* ───────────────── shell ───────────────── */

function useDeckStage() {
  const [index, setIndex] = useState(() => {
    if (typeof window === "undefined") return 0;
    const hashN = parseInt(window.location.hash.replace("#", ""), 10);
    if (!isNaN(hashN) && hashN >= 1 && hashN <= SLIDES) return hashN - 1;
    return 0;
  });
  const go = useCallback((n) => {
    const next = Math.max(0, Math.min(SLIDES - 1, n));
    setIndex(next);
    try { window.history.replaceState(null, "", `#${next + 1}`); } catch { /* noop */ }
  }, []);
  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) return;
      if (["ArrowRight", "PageDown", " "].includes(e.key)) { e.preventDefault(); go(index + 1); }
      else if (["ArrowLeft", "PageUp"].includes(e.key)) { e.preventDefault(); go(index - 1); }
      else if (e.key === "Home") { e.preventDefault(); go(0); }
      else if (e.key === "End") { e.preventDefault(); go(SLIDES - 1); }
      else if (e.key === "p" || e.key === "P") { e.preventDefault(); window.print(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, go]);
  return { index, go };
}

function DeckStage({ index, children }) {
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const resize = () => setScale(Math.min(window.innerWidth / 1920, window.innerHeight / 1080));
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);
  return (
    <div className="deck-outer">
      <div className="deck-canvas" style={{ transform: `translate(-50%, -50%) scale(${scale})` }} data-testid="deck-canvas">
        {children.map((c, i) => (
          <div key={i} className={`slide-shell ${i === index ? "is-active" : ""}`}
               aria-hidden={i !== index} data-testid={`slide-${i + 1}`}>
            {c}
          </div>
        ))}
      </div>
    </div>
  );
}

function Slide({ index, children, glow, grid }) {
  return (
    <section className="slide">
      {glow && <><span className="glow glow-tr" /><span className="glow glow-bl" /></>}
      {grid && <span className="grid-bg" />}
      <div className="slide-header">
        <div className="brand">
          <div className="brand-lockup"><VerqoLockup dark size="xl" /></div>
          <span className="brand-tag">wholesale instant</span>
        </div>
        <div className="mono-lime eyebrow"><span className="dot" />{LABELS[index]}</div>
      </div>
      <div className="slide-body">{children}</div>
      <div className="slide-footer">
        <div className="mono-dim">{String(index + 1).padStart(2, "0")} / {SLIDES}</div>
        <div className="mono-dim">Confidential</div>
      </div>
    </section>
  );
}

/* ───────────────── primitives ───────────────── */

const R = ({ d = 0, className = "", style, children, ...rest }) => (
  <div className={`r ${className}`} style={{ "--d": `${d}ms`, ...style }} {...rest}>{children}</div>
);

function Logo({ domain, name, sub, icon: Icon }) {
  return (
    <div className="logo-tile" title={name}>
      {domain ? (
        <img src={`https://icons.duckduckgo.com/ip3/${domain}.ico`} alt={name}
             onError={(e) => { e.currentTarget.src = `https://www.google.com/s2/favicons?domain=${domain}&sz=128`; }} />
      ) : (
        <div className="logo-icon">{Icon && <Icon size={22} />}</div>
      )}
      <div className="logo-meta">
        <span className="logo-name">{name}</span>
        {sub && <span className="logo-sub">{sub}</span>}
      </div>
    </div>
  );
}

function usePrinting() {
  const [printing, setPrinting] = useState(false);
  useEffect(() => {
    const on = () => setPrinting(true);
    const off = () => setPrinting(false);
    const mq = window.matchMedia("print");
    const onChange = (e) => setPrinting(e.matches);
    if (mq.matches) setPrinting(true);
    window.addEventListener("beforeprint", on);
    window.addEventListener("afterprint", off);
    mq.addEventListener?.("change", onChange);
    return () => {
      window.removeEventListener("beforeprint", on);
      window.removeEventListener("afterprint", off);
      mq.removeEventListener?.("change", onChange);
    };
  }, []);
  return printing;
}

function Count({ to, active, prefix = "", suffix = "", dur = 1300, decimals = 0 }) {
  const printing = usePrinting();
  const [v, setV] = useState(0);
  const raf = useRef();
  useEffect(() => {
    if (printing) { setV(to); return; }
    if (!active) { setV(0); return; }
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.max(0, Math.min(1, (t - t0) / dur));
      setV(to * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [active, to, dur, printing]);
  return <span>{prefix}{v.toFixed(decimals)}{suffix}</span>;
}

/* ───────────────── 01 Cover ───────────────── */

function Cover({ idx }) {
  return (
    <Slide index={idx} glow>
      <div className="cover-body">
        <R d={80} className="mono-lime cover-eyebrow"><span className="dot" />Wholesale banking for buyers &amp; sellers</R>
        <h1 className="cover-h1">
          <R d={200}>The modern</R>
          <R d={340}>operating system</R>
          <R d={480} className="lime sweep">for wholesale.</R>
        </h1>
        <R d={700} className="cover-foot">
          <div className="mono-dim">verqo.com</div>
          <div className="mono-dim">Seed round · 2026</div>
        </R>
      </div>
    </Slide>
  );
}

/* ───────────────── 02 Product / dashboard ───────────────── */

function Product({ active, idx }) {
  const views = [
    {
      side: "Seller portal", url: "app.verqo.com/seller",
      src: "/preview-screens/seller/dashboard-hero.jpeg",
      chips: [{ l: "Paid on day 1", v: "$127,400" }, { l: "Trade score", v: "94" }, { l: "Settlement", v: "instant" }],
    },
    {
      side: "Buyer portal", url: "app.verqo.com/buyer",
      src: "/preview-screens/buyer/dashboard-hero.jpeg",
      chips: [{ l: "Credit available", v: "$240,000" }, { l: "Terms granted", v: "Net-60" }, { l: "Approval", v: "60 sec" }],
    },
  ];
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!active) { setV(0); return; }
    const id = setInterval(() => setV((x) => 1 - x), 5000);
    return () => clearInterval(id);
  }, [active]);
  const cur = views[v];
  return (
    <Slide index={idx}>
      <div className="prod-head">
        <h2 className="h2 sm">This is <span className="lime">one</span> screen.</h2>
        <div className="prod-toggle">
          {views.map((w, i) => (
            <span key={i} className={`prod-pill ${i === v ? "is-on" : ""}`}>{w.side}</span>
          ))}
        </div>
      </div>
      <div className="shot-wrap">
        <R d={280} className="shot-frame float">
          <div className="shot-bar"><span /><span /><span /><div className="shot-url mono-dim">{cur.url}</div></div>
          <img key={cur.src} src={cur.src} alt={`Verqo ${cur.side}`} className="shot-img" />
        </R>
        <div className="shot-side">
          {cur.chips.map((c, i) => (
            <div key={`${v}-${i}`} className="float-chip fade">
              <div className="mono-dim tiny">{c.l}</div>
              <div className="chip-v lime">{c.v}</div>
            </div>
          ))}
          <div className="float-chip solid">
            <Zap size={22} />
            <span>3-minute setup</span>
          </div>
        </div>
      </div>
    </Slide>
  );
}

/* ───────────────── 03 Why us ───────────────── */

function WhyUs({ active, idx }) {
  return (
    <Slide index={idx} glow>
      <h2 className="h2">We didn&apos;t study this.<br /><span className="lime">We lived it.</span></h2>
      <div className="stat-row">
        {[
          { n: <Count active={active} to={12} suffix="+" />, l: "years in wholesale distribution" },
          { n: <Count active={active} to={5} prefix="$" suffix="M" />, l: "annual revenue we run today" },
          { n: <Count active={active} to={20} suffix="K" />, l: "SKUs managed" },
          { n: <Count active={active} to={300} suffix="+" />, l: "supplier relationships" },
        ].map((s, i) => (
          <R key={i} d={200 + i * 150} className="stat-cell">
            <div className="stat-n">{s.n}</div>
            <div className="mono-dim">{s.l}</div>
          </R>
        ))}
      </div>
      <R d={950} className="quote">
        Every delayed payment. Every painful reconciliation.<br />
        <span className="lime">We felt it first — then we built the fix.</span>
      </R>
    </Slide>
  );
}

/* ───────────────── 04 Problem ───────────────── */

function Problem({ idx }) {
  const tools = [
    { d: "faire.com", n: "Catalog" },
    { d: "stripe.com", n: "Payments" },
    { d: "shipstation.com", n: "Shipping" },
    { d: "allianz-trade.com", n: "Insurance" },
    { d: "bluevine.com", n: "Financing" },
    { d: "quickbooks.intuit.com", n: "Accounting" },
  ];
  return (
    <Slide index={idx} grid>
      <h2 className="h2"><span className="lime">Six</span> systems to sell <span className="lime">one</span> product.</h2>
      <div className="tangle">
        <svg className="tangle-svg" viewBox="0 0 1200 460" preserveAspectRatio="none">
          {[[300, 70], [300, 230], [300, 390], [900, 70], [900, 230], [900, 390]].map(([x, y], i) => (
            <path key={i} d={`M600 230 C ${(600 + x) / 2} 230, ${x} ${(230 + y) / 2}, ${x} ${y}`}
                  className="tangle-path" style={{ "--i": i }} />
          ))}
        </svg>
        <div className="tangle-grid">
          <div className="tangle-side">
            {tools.slice(0, 3).map((t, i) => (
              <R key={i} d={200 + i * 130}><Logo domain={t.d} name={t.n} /></R>
            ))}
          </div>
          <R d={80} className="tangle-hub">
            <Users size={34} className="lime-i" />
            <div className="hub-t">One seller</div>
            <div className="mono-dim tiny">switching tabs all day</div>
          </R>
          <div className="tangle-side right">
            {tools.slice(3).map((t, i) => (
              <R key={i} d={590 + i * 130}><Logo domain={t.d} name={t.n} /></R>
            ))}
          </div>
        </div>
      </div>
      <R d={1100} className="strap">None of them talk to each other.</R>
    </Slide>
  );
}

/* ───────────────── 05 Before verqo (stack map) ───────────────── */

function BeforeVerqo({ idx }) {
  const stack = [
    { d: "faire.com", n: "Faire", s: "15–25% commission" },
    { icon: Truck, n: "Freight forwarder", s: "call to schedule every shipment" },
    { icon: Landmark, n: "Manual wire / ACH", s: "keyed in by hand, days to clear" },
    { d: "quickbooks.intuit.com", n: "QuickBooks", s: "$90/mo · manual reconciling" },
    { icon: ShieldCheck, n: "Insurance broker", s: "shop for coverage, quote by quote" },
    { icon: Building2, n: "Banks", s: "loans that take months" },
  ];
  const out = [
    { icon: Clock, v: "30 hrs/week", l: "tab-switching and reconciling" },
    { icon: Banknote, v: "$10,000+/mo", l: "in tools, fees and commissions" },
    { icon: TrendingDown, v: "62-day DSO", l: "cash stuck after shipping" },
  ];
  return (
    <Slide index={idx}>
      <h2 className="h2 sm">Today: <span className="lime">juggling the stack.</span></h2>
      <div className="map">
        <div className="map-col">
          {stack.map((t, i) => (
            <R key={i} d={150 + i * 110} className="map-tile">
              <Logo domain={t.d} icon={t.icon} name={t.n} sub={t.s} />
            </R>
          ))}
        </div>
        <svg className="map-svg" viewBox="0 0 320 620" preserveAspectRatio="none">
          {[55, 155, 255, 355, 455, 555].map((y, i) => (
            <path key={i} d={`M0 ${y} C 160 ${y}, 180 310, 320 310`} className="map-path" style={{ "--i": i }} />
          ))}
        </svg>
        <div className="map-col out">
          {out.map((o, i) => (
            <R key={i} d={900 + i * 200} className="out-card">
              <o.icon size={26} className="lime-i" />
              <div>
                <div className="out-v">{o.v}</div>
                <div className="mono-dim tiny">{o.l}</div>
              </div>
            </R>
          ))}
        </div>
      </div>
    </Slide>
  );
}

/* ───────────────── 06 Getting paid ───────────────── */

function GettingPaid({ active, idx }) {
  return (
    <Slide index={idx}>
      <h2 className="h2">The product shipped.<br />The money is <span className="lime">stuck.</span></h2>
      <div className="paid-grid">
        <R d={200} className="paid-card">
          <div className="mono-dim tiny">Days waiting for payment</div>
          <div className="dso"><Count active={active} to={62} dur={1800} /><span className="dso-u">days</span></div>
          <div className="dso-bar"><span className="dso-fill" /></div>
          <div className="mono-dim tiny">Invoice #INV-9087 · $127,400</div>
        </R>
        <R d={340} className="paid-card">
          <div className="mono-dim tiny">Follow-ups sent</div>
          <div className="mails">
            {["Hey, just following up…", "Checking in on that invoice", "Second reminder", "Third reminder", "Calling instead…"].map((m, i) => (
              <div key={i} className="mail" style={{ "--i": i }}>
                <Mail size={16} /><span>{m}</span>
              </div>
            ))}
          </div>
        </R>
        <R d={480} className="paid-card">
          <div className="mono-dim tiny">Cash flow</div>
          <svg viewBox="0 0 320 160" className="flow-svg">
            <path d="M0 30 C 80 30, 120 120, 320 140" className="flow-path" />
          </svg>
          <div className="flow-l">Crushed.</div>
        </R>
      </div>
      <R d={900} className="strap">Sellers wait weeks. Sometimes months.</R>
    </Slide>
  );
}

/* ───────────────── 07 Blind risk ───────────────── */

function BlindRisk({ active, idx }) {
  const research = [
    { d: "dnb.com", n: "Dun & Bradstreet", s: "pull a report · $$ · days old" },
    { d: "experian.com", n: "Experian", s: "thin file on most SMB buyers" },
    { icon: PhoneCall, n: "Trade references", s: "email 3 suppliers · wait a week" },
    { icon: FileSearch, n: "Gut call on terms", s: "Net-30? Net-60? Guess." },
  ];
  return (
    <Slide index={idx} grid>
      <h2 className="h2 sm">You&apos;re making a million-dollar call on <span className="lime">a hunch.</span></h2>
      <div className="risk-grid">
        <R d={200} className="risk-card flip">
          <div className="flip-inner">
            <div className="flip-face">
              <div className="mono-dim tiny">New buyer</div>
              <div className="risk-name">Northline Retail Co.</div>
              <div className="mono-dim tiny">Wants Net-60 · $240,000</div>
              <div className="risk-q">?</div>
              <div className="mono-dim tiny">No history. No data. Ship anyway?</div>
            </div>
            <div className="flip-face back">
              <div className="mono-lime tiny"><span className="dot" />What no report showed you</div>
              {["Defaulted last week · Supplier A", "Defaulted · Supplier B", "45 days late · Supplier C"].map((t, i) => (
                <div key={i} className="risk-flag">{t}</div>
              ))}
              <div className="risk-late">You find out after you ship.</div>
            </div>
          </div>
        </R>
        <R d={500} className="risk-arrow">→</R>
        <div className="risk-col">
          <R d={560} className="mono-lime tiny"><span className="dot" />So you go hunting</R>
          {research.map((t, i) => (
            <R key={i} d={660 + i * 150}>
              <Logo domain={t.d} icon={t.icon} name={t.n} sub={t.s} />
            </R>
          ))}
          <R d={1300} className="risk-verdict">
            <AlertTriangle size={26} />
            <span>Days of work — and still no idea if they defaulted last week.</span>
          </R>
        </div>
      </div>
      {active && null}
    </Slide>
  );
}

/* ───────────────── 08 Solution ───────────────── */

function Solution({ idx }) {
  const wins = [
    { icon: Zap, t: "Paid instantly", d: "no chasing" },
    { icon: Gauge, t: "Live trade score", d: "every buyer, scored" },
    { icon: Layers, t: "One platform", d: "orders to freight" },
  ];
  return (
    <Slide index={idx} glow>
      <div className="reveal">
        <R d={100} className="reveal-kicker mono-lime"><span className="dot" />Today we announce</R>
        <R d={280} className="reveal-h">There is finally a solution.</R>
        <div className="burst">
          <span className="burst-ring r1" /><span className="burst-ring r2" /><span className="burst-ring r3" />
          <div className="reveal-mark">
            <div className="reveal-lockup"><VerqoLockup dark size="xl" /></div>
          </div>
        </div>
        <div className="reveal-row">
          {wins.map((w, i) => (
            <R key={i} d={1500 + i * 200} className="reveal-card">
              <w.icon size={26} className="lime-i" />
              <div className="reveal-t">{w.t}</div>
              <div className="mono-dim tiny">{w.d}</div>
            </R>
          ))}
        </div>
      </div>
    </Slide>
  );
}

/* ───────────────── 09 One click ───────────────── */

function OneClick({ active, idx }) {
  const acts = [
    { icon: Banknote, t: "Finance", r: "$127,400 funded · 4 seconds" },
    { icon: ShieldCheck, t: "Insure", r: "Allianz-insured · policy live" },
    { icon: Truck, t: "Ship", r: "Pallet booked · tracking live" },
    { icon: Wallet, t: "Get paid", r: "Wired to wallet · reconciled" },
  ];
  const [done, setDone] = useState([]);
  const printing = usePrinting();
  useEffect(() => {
    if (printing) { setDone([0, 1, 2, 3]); return; }
    if (!active) { setDone([]); return; }
    const timers = acts.map((_, i) => setTimeout(() => setDone((p) => [...new Set([...p, i])]), 900 + i * 700));
    return () => timers.forEach(clearTimeout);
  }, [active, printing]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Slide index={idx}>
      <h2 className="h2">Everything is <span className="lime">one click.</span></h2>
      <div className="click-grid">
        {acts.map((a, i) => {
          const on = done.includes(i);
          return (
            <R key={i} d={150 + i * 140}>
              <button className={`click-card ${on ? "is-on" : ""}`}
                      onClick={() => setDone((p) => (p.includes(i) ? p.filter((x) => x !== i) : [...p, i]))}
                      data-testid={`click-card-${a.t.toLowerCase().replace(" ", "-")}`}>
                <a.icon size={40} className="lime-i" />
                <div className="click-t">{a.t}</div>
                <div className="click-cta mono-dim tiny">{on ? "done" : "click"}</div>
                <div className="click-r">{a.r}</div>
                <span className="click-pulse" />
              </button>
            </R>
          );
        })}
      </div>
      <R d={800} className="strap">No calls. No paperwork. No chasing.</R>
    </Slide>
  );
}

/* ───────────────── 10 How it works ───────────────── */

function HowItWorks({ active, idx }) {
  const steps = [
    { t: "Buy", d: "Buyers discover suppliers and order on terms." },
    { t: "Sell", d: "Sellers approve the PO in one tap." },
    { t: "Get paid", d: "Cash hits the wallet the same day." },
    { t: "Ship", d: "Parcel, pallet or truckload — same screen." },
  ];
  const [cur, setCur] = useState(0);
  const printing = usePrinting();
  useEffect(() => {
    if (printing) { setCur(3); return; }
    if (!active) { setCur(0); return; }
    const id = setInterval(() => setCur((c) => (c + 1) % 4), 1800);
    return () => clearInterval(id);
  }, [active, printing]);
  return (
    <Slide index={idx}>
      <h2 className="h2">Four steps. <span className="lime">One system.</span></h2>
      <div className="rail">
        {steps.map((s, i) => (
          <div key={i} className={`rail-step ${i <= cur ? "is-on" : ""}`}>
            <div className="rail-dot">{i + 1}</div>
            <div className="rail-t">{s.t}</div>
            <div className="rail-d">{s.d}</div>
          </div>
        ))}
        <div className="rail-line"><span style={{ width: `${(cur / 3) * 100}%` }} /></div>
      </div>
      <R d={800} className="strap">You sell. You get paid. Done.</R>
    </Slide>
  );
}

/* ───────────────── 11 Banking at the core ───────────────── */

function Reconcile({ idx }) {
  const events = [
    { i: Layers, t: "Order" },
    { i: Truck, t: "Shipment" },
    { i: ShieldCheck, t: "Insurance" },
    { i: Banknote, t: "Financing" },
    { i: Wallet, t: "Buyer payment" },
  ];
  return (
    <Slide index={idx} grid>
      <h2 className="h2 sm">Every dollar lands <span className="lime">already matched.</span></h2>
      <div className="lanes">
        <R d={200} className="lane bad">
          <div className="lane-tag mono-dim tiny">Everyone else · bank sits outside</div>
          <div className="lane-row">
            {events.map((e, i) => (
              <div key={i} className="lane-chip"><e.i size={20} /><span>{e.t}</span></div>
            ))}
            <div className="lane-gap">?</div>
            <div className="lane-end">Bank statement</div>
          </div>
          <div className="lane-note bad-note">Matched by hand · spreadsheets · weeks late</div>
        </R>
        <R d={520} className="lane good">
          <div className="lane-tag mono-lime tiny"><span className="dot" />verqo · bank is the core</div>
          <div className="lane-row">
            {events.map((e, i) => (
              <div key={i} className="lane-chip on" style={{ "--i": i }}><e.i size={20} /><span>{e.t}</span></div>
            ))}
            <div className="lane-arrow">→</div>
            <div className="lane-end lime-end"><Building2 size={22} /><span>verqo ledger</span></div>
          </div>
          <div className="lane-note good-note">Auto-reconciled to the order the moment money moves</div>
        </R>
      </div>
    </Slide>
  );
}

/* ───────────────── 12 Who we serve ───────────────── */

function WhoFor({ active, idx }) {
  const cats = [
    { t: "Beauty & personal care", i: Sparkles },
    { t: "Food & beverage", i: UtensilsCrossed },
    { t: "Household & cleaning", i: SprayCan },
    { t: "Apparel & accessories", i: Shirt },
    { t: "Pet supplies", i: PawPrint },
    { t: "Supplements & wellness", i: Pill },
    { t: "Toys & baby", i: Baby },
    { t: "Electronics accessories", i: Headphones },
  ];
  return (
    <Slide index={idx}>
      <h2 className="h2 sm">Built for the <span className="lime">$1M–$200M</span> seller.</h2>
      <R d={200} className="scale-wrap">
        <div className="scale-bar"><span className="scale-band" /></div>
        <div className="scale-marks mono-dim tiny">
          <span>$0</span><span className="lime">$1M</span><span className="lime">$200M</span><span>$1B+</span>
        </div>
      </R>
      <div className="cat-grid">
        {cats.map((c, i) => (
          <R key={i} d={520 + i * 90} className="cat-tile">
            <c.i size={22} className="lime-i" />
            <span>{c.t}</span>
          </R>
        ))}
      </div>
      <R d={1300} className="strap">Mid-market CPG brands: too big for spreadsheets, too small for SAP.</R>
      {active && null}
    </Slide>
  );
}

/* ───────────────── 18 What's next ───────────────── */

function Coming({ idx }) {
  const items = [
    { i: Zap, t: "Global instant rails", d: "Any buyer, any country — paid in one click, fully secured." },
    { i: Users, t: "Open marketplace", d: "Find suppliers in any category. Both sides see their trust score." },
    { i: Building2, t: "Enterprise portals", d: "Retailers and manufacturers move their whole buyer base onto verqo." },
  ];
  return (
    <Slide index={idx}>
      <h2 className="h2 sm">Where we go <span className="lime">from here.</span></h2>
      <div className="next-row">
        {items.map((s, i) => (
          <R key={i} d={220 + i * 220} className="next-card">
            <div className="next-step mono-dim tiny">0{i + 1}</div>
            <s.i size={34} className="lime-i" />
            <div className="tile-t">{s.t}</div>
            <div className="tile-d">{s.d}</div>
          </R>
        ))}
        <div className="next-line"><span /></div>
      </div>
      <R d={1000} className="strap">One network. Every buyer and seller on earth.</R>
    </Slide>
  );
}

/* ───────────────── 19 The vision ───────────────── */

function VisionOwn({ idx }) {
  const owners = [
    { d: "ramp.com", n: "Ramp", s: "corporate cards" },
    { d: "stripe.com", n: "Stripe", s: "online checkout" },
    { d: "shopify.com", n: "Shopify", s: "ecommerce" },
  ];
  return (
    <Slide index={idx} glow>
      <h2 className="h2 sm">Every category has an owner.<br /><span className="lime">Wholesale has none.</span></h2>
      <div className="own-row">
        {owners.map((o, i) => (
          <R key={i} d={200 + i * 170} className="own-card">
            <img src={`https://icons.duckduckgo.com/ip3/${o.d}.ico`} alt={o.n}
                 onError={(e) => { e.currentTarget.src = `https://www.google.com/s2/favicons?domain=${o.d}&sz=128`; }} />
            <div className="own-n">{o.n}</div>
            <div className="mono-dim tiny">{o.s}</div>
          </R>
        ))}
        <R d={800} className="own-card is-verqo">
          <div className="own-lock"><VerqoLockup dark size="lg" /></div>
          <div className="own-n lime">Wholesale</div>
          <div className="mono-dim tiny">$32T · unowned</div>
        </R>
      </div>
      <R d={1100} className="quote">
        We intend to be the <span className="lime">#1 wholesale operating system</span> on the planet.
      </R>
    </Slide>
  );
}

/* ───────────────── 12 Why now ───────────────── */

function WhyNow({ idx }) {
  const items = [
    { t: "Instant money", d: "RTP + FedNow made same-day settlement normal.", i: Zap },
    { t: "AI underwriting", d: "Credit decisions in 60 seconds, not 6 weeks.", i: Gauge },
    { t: "Embedded rails", d: "Banking, insurance and freight are now APIs.", i: Layers },
  ];
  return (
    <Slide index={idx}>
      <h2 className="h2">Wholesale is having its<br /><span className="lime">Shopify moment.</span></h2>
      <div className="three">
        {items.map((s, i) => (
          <R key={i} d={200 + i * 180} className="tile">
            <s.i size={34} className="lime-i" />
            <div className="tile-t">{s.t}</div>
            <div className="tile-d">{s.d}</div>
          </R>
        ))}
      </div>
    </Slide>
  );
}

/* ───────────────── 13 Market ───────────────── */

function Market({ active, idx }) {
  return (
    <Slide index={idx}>
      <h2 className="h2 sm">A <span className="lime">$32 trillion</span> market still on paper.</h2>
      <div className="market">
        <div className="market-nums">
          {[
            { v: <Count active={active} to={32} prefix="$" suffix="T" />, l: "US + global B2B wholesale trade" },
            { v: <Count active={active} to={3.1} decimals={1} prefix="$" suffix="T" />, l: "trapped in unpaid receivables" },
            { v: <Count active={active} to={62} suffix=" days" />, l: "average days sellers wait to get paid" },
          ].map((m, i) => (
            <R key={i} d={200 + i * 170} className="market-cell">
              <div className="market-v">{m.v}</div>
              <div className="mono-dim">{m.l}</div>
            </R>
          ))}
        </div>
        <R d={600} className="digi">
          <div className="mono-lime tiny"><span className="dot" />Digitized today</div>
          <div className="donut">
            <svg viewBox="0 0 200 200">
              <circle cx="100" cy="100" r="82" className="donut-track" />
              <circle cx="100" cy="100" r="82" className="donut-fill" />
            </svg>
            <div className="donut-v"><Count active={active} to={7} suffix="%" /></div>
          </div>
          <div className="mono-dim tiny">93% of wholesale still runs on email, PDFs and phone calls</div>
        </R>
      </div>
    </Slide>
  );
}

/* ───────────────── 14 Customer #1 ───────────────── */

function CustomerOne({ active, idx }) {
  return (
    <Slide index={idx} glow>
      <h2 className="h2">We are <span className="lime">customer #1.</span></h2>
      <div className="cust">
        <R d={200} className="cust-card">
          <div className="cust-logo">
            <img src="/brand/brnddirect.png" alt="brnddirect" />
            <div>
              <div className="cust-n">brnddirect.com</div>
              <div className="mono-dim tiny">Wholesale distribution · live on verqo</div>
            </div>
          </div>
          <div className="cust-stats">
            <div><div className="cust-v"><Count active={active} to={5} prefix="$" suffix="M" /></div><div className="mono-dim tiny">annual revenue</div></div>
            <div><div className="cust-v"><Count active={active} to={20} suffix="K" /></div><div className="mono-dim tiny">SKUs</div></div>
            <div><div className="cust-v lime">Day 1</div><div className="mono-dim tiny">payouts</div></div>
          </div>
        </R>
        <R d={500} className="pipe">
          <div className="mono-lime tiny"><span className="dot" />Pipeline ready to sign</div>
          <div className="pipe-grid">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="pipe-av" style={{ "--i": i }}>
                <Building2 size={20} />
              </div>
            ))}
          </div>
          <div className="pipe-v"><Count active={active} to={10} suffix="+" /> <span className="pipe-l">sellers waiting</span></div>
        </R>
      </div>
    </Slide>
  );
}

/* ───────────────── 15 Business ───────────────── */

function Business({ idx }) {
  const rev = [
    { t: "Subscription", d: "$99 → $1,999/mo", i: Layers },
    { t: "Financing", d: "credit markup", i: Banknote },
    { t: "Factoring", d: "origination fee", i: TrendingDown },
    { t: "Payments", d: "rail spread", i: Zap },
    { t: "Cards", d: "interchange", i: CreditCard },
    { t: "Insurance", d: "commission", i: ShieldCheck },
    { t: "Shipping", d: "carrier margin", i: Truck },
    { t: "Float", d: "idle balances", i: Wallet },
  ];
  return (
    <Slide index={idx}>
      <h2 className="h2 sm">One transaction. <span className="lime">Eight ways we earn.</span></h2>
      <div className="rev-grid">
        {rev.map((r, i) => (
          <R key={i} d={120 + i * 80} className="rev-card">
            <r.i size={24} className="lime-i" />
            <div className="rev-t">{r.t}</div>
            <div className="mono-dim tiny">{r.d}</div>
          </R>
        ))}
      </div>
      <div className="align-row">
        <R d={900} className="align-card">
          <div className="mono-lime tiny"><span className="dot" />We only grow when they grow</div>
          <div className="bars">
            {[26, 40, 55, 72, 92].map((h, i) => (
              <div key={i} className="bar-pair" style={{ "--i": i }}>
                <span className="bar vol" style={{ "--h": `${h}%` }} />
                <span className="bar rev" style={{ "--h": `${h * 0.62}%` }} />
              </div>
            ))}
          </div>
          <div className="bars-key mono-dim tiny"><span><i className="k vol" />buyer volume</span><span><i className="k rev" />verqo revenue</span></div>
        </R>
        <R d={1150} className="align-card">
          <div className="mono-lime tiny"><span className="dot" />Priced per buyer</div>
          <div className="seat-row">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="seat" style={{ "--i": i }}><Users size={18} /></div>
            ))}
            <div className="seat plus">+</div>
          </div>
          <div className="seat-note">Sellers pay for every buyer they transact with — more buyers, more seats, more volume.</div>
        </R>
      </div>
    </Slide>
  );
}

/* ───────────────── 16 Differentiation ───────────────── */

function Differentiation({ idx }) {
  const pts = [
    { d: "shipstation.com", n: "ShipStation", x: 16, y: 8 },
    { d: "quickbooks.intuit.com", n: "QuickBooks", x: 32, y: 20 },
    { d: "faire.com", n: "Faire", x: 58, y: 34 },
    { d: "joor.com", n: "Joor", x: 68, y: 16 },
    { d: "wholesail.com", n: "Wholesail", x: 46, y: 52 },
    { d: "shopify.com", n: "Shopify", x: 34, y: 38 },
    { d: "stripe.com", n: "Stripe", x: 12, y: 62 },
    { d: "bluevine.com", n: "Bluevine", x: 24, y: 76 },
    { d: "allianz-trade.com", n: "Allianz", x: 6, y: 44 },
    { d: "trevipay.com", n: "TreviPay", x: 44, y: 70 },
  ];
  return (
    <Slide index={idx}>
      <div className="vision-head">
        <h2 className="h2 sm">Everyone owns a slice. <span className="lime">We own the flow.</span></h2>
      </div>
      <div className="quad">
        <div className="quad-y mono-dim">Moves the money</div>
        <div className="quad-x mono-dim">Runs the whole wholesale workflow</div>
        <div className="quad-plot">
          <span className="quad-axis-y" /><span className="quad-axis-x" />
          {pts.map((p, i) => (
            <R key={i} d={200 + i * 100} className="quad-pt" style={{ left: `${p.x}%`, bottom: `${p.y}%` }}>
              <img src={`https://icons.duckduckgo.com/ip3/${p.d}.ico`} alt={p.n}
                   onError={(e) => { e.currentTarget.src = `https://www.google.com/s2/favicons?domain=${p.d}&sz=128`; }} />
              <span>{p.n}</span>
            </R>
          ))}
          <R d={1300} className="quad-pt is-verqo" style={{ left: "84%", bottom: "84%" }}>
            <VerqoLockup size="md" boxColor="#0b1914" glyphColor="#dcff3a" wordmarkColor="#0b1914" />
          </R>
        </div>
      </div>
    </Slide>
  );
}

/* ───────────────── 17 Use of proceeds ───────────────── */

function Proceeds({ active, idx }) {
  const uses = [
    { v: "$3M", t: "Bank reserve", d: "collateral that unlocks Day-1 payouts at scale", i: Building2 },
    { v: "$2M", t: "Operations, sales & marketing", d: "team, GTM and wholesale corridor expansion", i: Rocket },
  ];
  return (
    <Slide index={idx} glow>
      <h2 className="h2 sm">Where the money goes.</h2>
      <div className="proceeds">
        <R d={150} className="pro-circle">
          <div className="pro-amt">$<Count active={active} to={5} />M</div>
          <div className="mono-dim tiny">Seed</div>
        </R>
        <svg className="pro-svg" viewBox="0 0 200 400">
          <path d="M0 200 C 90 200, 110 90, 200 90" className="pro-path" style={{ "--i": 0 }} />
          <path d="M0 200 C 90 200, 110 310, 200 310" className="pro-path" style={{ "--i": 1 }} />
        </svg>
        <div className="pro-col">
          {uses.map((u, i) => (
            <R key={i} d={700 + i * 250} className="pro-card">
              <u.i size={30} className="lime-i" />
              <div>
                <div className="pro-v">{u.v}</div>
                <div className="pro-t">{u.t}</div>
                <div className="mono-dim tiny">{u.d}</div>
              </div>
            </R>
          ))}
        </div>
      </div>
    </Slide>
  );
}

/* ───────────────── 18 Mission ───────────────── */

function Mission({ idx }) {
  return (
    <Slide index={idx} glow>
      <div className="mission">
        <h2 className="mission-h">
          <R d={160}>Wholesale should feel</R>
          <R d={320} className="lime sweep">like ecommerce.</R>
        </h2>
        <R d={620} className="mission-line">
          Make B2B commerce as simple, connected and instant as buying online — less friction,
          fewer people, faster money.
        </R>
        <div className="mission-row">
          <R d={900} className="mission-card">
            <Zap size={26} className="lime-i" />
            <div className="mission-v">Instant</div>
            <div className="mono-dim tiny">money moves the moment the deal does</div>
          </R>
          <R d={1050} className="mission-card">
            <ShieldCheck size={26} className="lime-i" />
            <div className="mission-v">Trusted</div>
            <div className="mono-dim tiny">every counterparty scored and covered</div>
          </R>
          <R d={1200} className="mission-card lime-card">
            <Rocket size={26} className="lime-i" />
            <div className="mission-v lime">Effortless</div>
            <div className="mono-dim tiny">three minutes from signup to paid</div>
          </R>
        </div>
      </div>
    </Slide>
  );
}

/* ───────────────── 22 Closing ───────────────── */

function Closing({ idx }) {
  return (
    <Slide index={idx} glow>
      <div className="close-body">
        <h2 className="close-h">
          <R d={340}>Wholesale, finally moving at</R>
          <R d={500} className="lime sweep">the speed of business.</R>
        </h2>
        <R d={800} className="close-thanks">Thank you.</R>
        <R d={1000} className="close-foot">
          <div className="mono-dim">verqo.com</div>
          <div className="mono-lime"><span className="dot" />Raising $5M seed</div>
        </R>
      </div>
    </Slide>
  );
}

/* ───────────────── chrome ───────────────── */

function DeckChrome({ index, go }) {
  const [visible, setVisible] = useState(true);
  const [hint, setHint] = useState(false);
  useEffect(() => {
    let timer;
    const wake = () => {
      setVisible(true);
      clearTimeout(timer);
      timer = setTimeout(() => setVisible(false), 2500);
    };
    wake();
    ["mousemove", "keydown", "touchstart"].forEach((e) => window.addEventListener(e, wake));
    return () => {
      clearTimeout(timer);
      ["mousemove", "keydown", "touchstart"].forEach((e) => window.removeEventListener(e, wake));
    };
  }, []);
  const print = () => {
    setHint(true);
    setTimeout(() => { window.print(); setHint(false); }, 900);
  };
  return (
    <div className={`deck-chrome ${visible ? "is-visible" : ""}`}>
      {hint && (
        <div className="print-hint" data-testid="print-hint">
          Choose <b>Landscape</b> and tick <b>Background graphics</b> for the full-colour deck.
        </div>
      )}
      <div className="chrome-progress"><div className="chrome-bar" style={{ width: `${((index + 1) / SLIDES) * 100}%` }} /></div>
      <div className="chrome-row">
        <button className="chrome-btn" onClick={() => go(index - 1)} disabled={index === 0} data-testid="deck-prev">←</button>
        <div className="chrome-idx mono-dim">{String(index + 1).padStart(2, "0")} / {SLIDES}</div>
        <button className="chrome-btn" onClick={() => go(index + 1)} disabled={index === SLIDES - 1} data-testid="deck-next">→</button>
        <button className="chrome-btn wide" onClick={print} data-testid="deck-print">Print PDF</button>
      </div>
    </div>
  );
}

/* ───────────────── page ───────────────── */

const SLIDE_LIST = [Cover, WhyUs, Problem, BeforeVerqo, GettingPaid, BlindRisk,
  Solution, Product, OneClick, HowItWorks, Reconcile, WhoFor, WhyNow, Market, CustomerOne,
  Business, Differentiation, Coming, VisionOwn, Proceeds, Mission, Closing];

export default function InvestorPitchV2() {
  const { index, go } = useDeckStage();
  useEffect(() => {
    const id = "verqo-deck-fonts";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.rel = "stylesheet"; link.id = id;
    link.href = "https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,500;12..96,600;12..96,700&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=JetBrains+Mono:wght@400;500;600&display=swap";
    document.head.append(link);
  }, []);
  return (
    <>
      <style>{styles}</style>
      <DeckStage index={index}>
        {SLIDE_LIST.map((C, i) => <C key={i} active={index === i} idx={i} />)}
      </DeckStage>
      <DeckChrome index={index} go={go} />
    </>
  );
}

/* ───────────────── styles ───────────────── */

const styles = `
:root {
  --ink:#0b1914; --ink-2:#0a1f1c; --cream:#f7f6f3;
  --dim:rgba(247,246,243,.62); --dim-2:rgba(247,246,243,.42);
  --lime:#dfff3d; --lime-soft:rgba(223,255,61,.10);
  --line:rgba(223,255,61,.20); --line-3:rgba(255,255,255,.07);
  --font-display:"Bricolage Grotesque",ui-sans-serif,system-ui,sans-serif;
  --font-body:"DM Sans",ui-sans-serif,system-ui,sans-serif;
  --font-mono:"JetBrains Mono",ui-monospace,monospace;
}
.deck-outer{position:fixed;inset:0;background:#000;overflow:hidden;font-family:var(--font-body);color:var(--cream);}
.deck-canvas{position:absolute;top:50%;left:50%;width:1920px;height:1080px;transform-origin:center center;}
.slide-shell{position:absolute;inset:0;opacity:0;pointer-events:none;transition:opacity .3s ease;}
.slide-shell.is-active{opacity:1;pointer-events:auto;}
.slide{position:absolute;inset:0;background:var(--ink);padding:52px 96px;display:flex;flex-direction:column;overflow:hidden;}
.slide-header{display:flex;align-items:center;justify-content:space-between;padding-bottom:16px;border-bottom:1px solid var(--line-3);min-height:70px;}
.brand{display:flex;align-items:center;gap:74px;}
.brand-lockup{transform:scale(1.55);transform-origin:left center;}
.brand-tag{font-family:var(--font-mono);font-size:14px;letter-spacing:.16em;text-transform:uppercase;color:var(--dim-2);padding-left:18px;border-left:1px solid var(--line-3);}
.brand-mark{width:50px;height:50px;border-radius:12px;border:1.5px solid var(--lime);color:var(--lime);display:grid;place-items:center;font-family:var(--font-display);font-size:30px;}
.brand-mark.sm{width:26px;height:26px;border-radius:7px;font-size:16px;border-width:1px;}
.brand-mark.lg{width:96px;height:96px;border-radius:24px;font-size:60px;}
.brand-name{font-weight:500;font-size:30px;letter-spacing:-.015em;}
.eyebrow{display:inline-flex;align-items:center;}
.mono-lime,.mono-dim{font-family:var(--font-mono);font-weight:500;letter-spacing:.14em;text-transform:uppercase;}
.mono-lime{color:var(--lime);font-size:16px;} .mono-dim{color:var(--dim-2);font-size:16px;}
.mono-lime.tiny,.mono-dim.tiny{font-size:12px;letter-spacing:.12em;}
.dot{width:6px;height:6px;border-radius:50%;background:var(--lime);display:inline-block;margin-right:14px;}
.slide-body{flex:1;display:flex;flex-direction:column;padding-top:34px;padding-bottom:18px;position:relative;min-height:0;}
.slide-footer{display:flex;justify-content:space-between;padding-top:16px;border-top:1px solid var(--line-3);min-height:44px;}
.lime{color:var(--lime);} .lime-i{color:var(--lime);}
.glow{position:absolute;pointer-events:none;width:620px;height:620px;background:radial-gradient(closest-side,rgba(223,255,61,.20),transparent 70%);filter:blur(20px);animation:drift 14s ease-in-out infinite alternate;}
.glow-tr{top:-200px;right:-200px;} .glow-bl{bottom:-220px;left:-200px;animation-delay:-6s;}
@keyframes drift{from{transform:translate3d(0,0,0) scale(1);}to{transform:translate3d(-60px,40px,0) scale(1.15);}}
.grid-bg{position:absolute;inset:0;pointer-events:none;background-image:linear-gradient(rgba(255,255,255,.03) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.03) 1px,transparent 1px);background-size:80px 80px;}

/* reveal */
.r{opacity:0;transform:translateY(22px);}
.slide-shell.is-active .r{animation:rise .75s cubic-bezier(.2,.85,.25,1) forwards;animation-delay:var(--d,0ms);}
@keyframes rise{to{opacity:1;transform:none;}}

/* typography */
.h2{font-family:var(--font-display);font-weight:600;font-size:104px;line-height:.98;letter-spacing:-.03em;}
.h2.sm{font-size:76px;}
.sub{font-size:28px;color:var(--dim);margin-top:18px;}
.strap{margin-top:auto;font-family:var(--font-display);font-size:40px;color:var(--dim);letter-spacing:-.02em;}
.quote{margin-top:auto;font-family:var(--font-display);font-size:44px;line-height:1.2;letter-spacing:-.02em;border-left:3px solid var(--lime);padding-left:32px;}

/* cover */
.cover-body{display:flex;flex-direction:column;height:100%;}
.cover-eyebrow{margin-top:20px;}
.cover-h1{font-family:var(--font-display);font-weight:600;font-size:158px;line-height:1.0;letter-spacing:-.035em;margin-top:44px;}
.sweep{position:relative;display:inline-block;}
.slide-shell.is-active .sweep::after{content:"";position:absolute;left:0;bottom:-14px;height:6px;background:var(--lime);border-radius:4px;width:0;animation:sweep .9s .9s cubic-bezier(.2,.9,.2,1) forwards;}
@keyframes sweep{to{width:100%;}}
.cover-foot{margin-top:auto;display:flex;justify-content:space-between;}

/* product / shot */
.prod-head{display:flex;align-items:center;justify-content:space-between;gap:32px;}
.prod-toggle{display:flex;gap:12px;}
.prod-pill{font-family:var(--font-mono);font-size:13px;letter-spacing:.14em;text-transform:uppercase;padding:12px 22px;border-radius:999px;border:1px solid var(--line-3);color:var(--dim-2);transition:all .4s ease;}
.prod-pill.is-on{background:var(--lime);border-color:var(--lime);color:var(--ink);}
.shot-wrap{margin-top:56px;display:grid;grid-template-columns:1fr 340px;gap:44px;align-items:start;flex:1;min-height:0;}
.shot-frame{border-radius:18px;overflow:hidden;border:1px solid rgba(255,255,255,.12);box-shadow:0 50px 120px rgba(0,0,0,.6);background:var(--ink-2);max-height:760px;display:flex;flex-direction:column;}
.float{animation:none;}
.slide-shell.is-active .float{animation:rise .8s cubic-bezier(.2,.85,.25,1) forwards,bob 7s 1s ease-in-out infinite;}
@keyframes bob{0%,100%{transform:translateY(0);}50%{transform:translateY(-14px);}}
.shot-bar{display:flex;align-items:center;gap:8px;padding:12px 18px;background:rgba(255,255,255,.04);border-bottom:1px solid var(--line-3);}
.shot-bar span{width:10px;height:10px;border-radius:50%;background:rgba(255,255,255,.18);}
.shot-url{margin-left:16px;font-size:12px;}
.shot-frame img{display:block;width:100%;height:auto;max-height:640px;object-fit:cover;object-position:top center;animation:fadeIn .8s ease;}
@keyframes fadeIn{from{opacity:0;}to{opacity:1;}}
.float-chip.fade{animation:fadeIn .7s ease;}
.logo-icon{width:40px;height:40px;border-radius:9px;background:var(--lime-soft);border:1px solid var(--line);color:var(--lime);display:grid;place-items:center;flex-shrink:0;}
.shot-side{display:flex;flex-direction:column;gap:20px;}
.float-chip{background:var(--ink-2);border:1px solid var(--line-3);border-radius:16px;padding:22px 24px;}
.float-chip.solid{background:var(--lime);color:var(--ink);display:flex;align-items:center;gap:12px;font-family:var(--font-display);font-size:26px;font-weight:600;}
.chip-v{font-family:var(--font-display);font-size:46px;font-weight:600;letter-spacing:-.02em;margin-top:6px;}

/* stats */
.stat-row{display:grid;grid-template-columns:repeat(4,1fr);gap:28px;margin-top:56px;}
.stat-cell{display:flex;flex-direction:column;gap:10px;}
.stat-n{font-family:var(--font-display);font-weight:600;font-size:96px;line-height:.9;letter-spacing:-.035em;color:var(--lime);}

/* tangle (problem) */
.tangle{position:relative;flex:1;margin-top:24px;min-height:0;}
.tangle-svg{position:absolute;inset:0;width:100%;height:100%;}
.tangle-path{fill:none;stroke:rgba(255,255,255,.22);stroke-width:1.5;stroke-dasharray:6 8;stroke-dashoffset:0;}
.slide-shell.is-active .tangle-path{animation:dash 3s linear infinite;animation-delay:calc(var(--i)*.2s);}
@keyframes dash{to{stroke-dashoffset:-140;}}
.tangle-grid{position:absolute;inset:0;display:grid;grid-template-columns:1fr 340px 1fr;align-items:center;justify-items:center;}
.tangle-side{display:flex;flex-direction:column;gap:22px;}
.tangle-hub{position:relative;width:300px;height:300px;border-radius:50%;border:1.5px solid var(--lime);background:radial-gradient(closest-side,rgba(223,255,61,.14),rgba(11,25,20,.9));display:grid;place-items:center;gap:6px;text-align:center;z-index:3;}
.slide-shell.is-active .tangle-hub{animation:rise .7s forwards,pulse 3.4s 1s ease-in-out infinite;}
@keyframes pulse{0%,100%{box-shadow:0 0 0 0 rgba(223,255,61,.16);}50%{box-shadow:0 0 0 26px rgba(223,255,61,0);}}
.hub-t{font-family:var(--font-display);font-size:32px;font-weight:600;}
.logo-tile{display:flex;align-items:center;gap:14px;background:var(--ink-2);border:1px solid var(--line-3);border-radius:14px;padding:14px 20px;min-width:250px;}
.logo-tile img{width:40px;height:40px;border-radius:9px;background:#fff;object-fit:contain;padding:4px;}
.logo-meta{display:flex;flex-direction:column;}
.logo-name{font-family:var(--font-display);font-size:24px;font-weight:600;letter-spacing:-.01em;}
.logo-sub{font-family:var(--font-mono);font-size:12px;color:var(--dim-2);letter-spacing:.08em;text-transform:uppercase;margin-top:3px;}
.tool-node{z-index:2;}

/* map (before verqo) */
.map{flex:1;display:grid;grid-template-columns:1fr 320px 1fr;align-items:center;gap:20px;margin-top:20px;min-height:0;}
.map-col{display:flex;flex-direction:column;gap:14px;}
.map-col.out{gap:24px;}
.map-tile{}
.map-svg{width:100%;height:620px;}
.map-path{fill:none;stroke:rgba(223,255,61,.35);stroke-width:1.5;stroke-dasharray:600;stroke-dashoffset:600;}
.slide-shell.is-active .map-path{animation:draw 1.1s ease forwards;animation-delay:calc(.4s + var(--i)*.14s);}
@keyframes draw{to{stroke-dashoffset:0;}}
.out-card{display:flex;gap:18px;align-items:center;background:var(--lime-soft);border:1px solid var(--line);border-radius:18px;padding:26px 28px;}
.out-v{font-family:var(--font-display);font-size:46px;font-weight:600;letter-spacing:-.02em;line-height:1;}

/* getting paid */
.paid-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:26px;margin-top:34px;}
.paid-card{background:var(--ink-2);border:1px solid var(--line-3);border-radius:20px;padding:30px;min-height:360px;}
.dso{font-family:var(--font-display);font-size:100px;font-weight:600;letter-spacing:-.04em;line-height:1;margin-top:14px;color:var(--lime);display:flex;align-items:baseline;gap:14px;}
.dso-u{font-size:32px;color:var(--dim-2);}
.dso-bar{height:10px;border-radius:999px;background:rgba(255,255,255,.08);margin:26px 0 14px;overflow:hidden;}
.dso-fill{display:block;height:100%;background:var(--lime);width:0;}
.slide-shell.is-active .dso-fill{animation:grow 1.8s .2s ease forwards;}
@keyframes grow{to{width:92%;}}
.mails{display:flex;flex-direction:column;gap:12px;margin-top:20px;}
.mail{display:flex;align-items:center;gap:12px;background:rgba(255,255,255,.05);border-radius:12px;padding:14px 16px;font-size:17px;color:var(--dim);opacity:0;transform:translateX(-14px);}
.slide-shell.is-active .mail{animation:slideIn .5s forwards;animation-delay:calc(.6s + var(--i)*.35s);}
@keyframes slideIn{to{opacity:1;transform:none;}}
.flow-svg{width:100%;height:170px;margin-top:14px;}
.flow-path{fill:none;stroke:#ff6b5e;stroke-width:4;stroke-dasharray:420;stroke-dashoffset:420;}
.slide-shell.is-active .flow-path{animation:draw 1.6s .5s ease forwards;}
.flow-l{font-family:var(--font-display);font-size:44px;font-weight:600;color:#ff6b5e;letter-spacing:-.02em;}

/* blind risk */
.risk-grid{display:grid;grid-template-columns:1fr 90px 1fr;align-items:center;gap:24px;margin-top:34px;flex:1;min-height:0;}
.risk-card{background:var(--ink-2);border:1px solid var(--line-3);border-radius:22px;padding:34px;min-height:430px;}
.risk-card.lime-card{background:var(--lime-soft);border-color:var(--line);display:flex;flex-direction:column;gap:14px;}
.flip{perspective:1400px;background:none;border:none;padding:0;}
.flip-inner{position:relative;width:100%;height:430px;transform-style:preserve-3d;}
.slide-shell.is-active .flip-inner{animation:flip 1s 1.6s cubic-bezier(.6,.05,.3,1) forwards;}
@keyframes flip{to{transform:rotateY(180deg);}}
.flip-face{position:absolute;inset:0;backface-visibility:hidden;background:var(--ink-2);border:1px solid var(--line-3);border-radius:22px;padding:34px;display:flex;flex-direction:column;gap:12px;}
.flip-face.back{transform:rotateY(180deg);border-color:rgba(255,107,94,.5);}
.risk-name{font-family:var(--font-display);font-size:44px;font-weight:600;letter-spacing:-.02em;}
.risk-q{font-family:var(--font-display);font-size:120px;color:var(--lime);line-height:1;margin:auto 0;}
.risk-flag{margin-top:6px;padding:16px 20px;border-radius:14px;background:rgba(255,107,94,.12);color:#ff9c92;font-size:20px;}
.risk-arrow{font-family:var(--font-mono);font-size:44px;color:var(--dim-2);text-align:center;}
.risk-col{display:flex;flex-direction:column;gap:14px;}
.risk-col .logo-tile{min-width:0;width:100%;padding:16px 22px;}
.risk-late{margin-top:auto;font-family:var(--font-display);font-size:26px;color:#ff9c92;letter-spacing:-.01em;}
.risk-verdict{display:flex;align-items:center;gap:16px;margin-top:8px;padding:22px 26px;border-radius:18px;background:rgba(255,107,94,.12);border:1px solid rgba(255,107,94,.35);color:#ff9c92;font-family:var(--font-display);font-size:26px;letter-spacing:-.01em;line-height:1.2;}

/* solution reveal */
.reveal{display:flex;flex-direction:column;height:100%;align-items:center;text-align:center;}
.reveal-kicker{margin-top:6px;display:inline-flex;align-items:center;}
.reveal-h{font-family:var(--font-display);font-weight:600;font-size:92px;line-height:1.02;letter-spacing:-.035em;margin-top:20px;}
.burst{position:relative;flex:1;min-height:0;width:100%;display:grid;place-items:center;}
.burst-ring{position:absolute;width:340px;height:340px;border-radius:50%;border:1.5px solid rgba(223,255,61,.45);opacity:0;}
.slide-shell.is-active .burst-ring{animation:ripple 2.6s ease-out infinite;}
.slide-shell.is-active .burst-ring.r2{animation-delay:.7s;}
.slide-shell.is-active .burst-ring.r3{animation-delay:1.4s;}
@keyframes ripple{0%{opacity:.7;transform:scale(.55);}100%{opacity:0;transform:scale(1.9);}}
.reveal-mark{position:relative;z-index:2;display:flex;flex-direction:column;align-items:center;gap:44px;opacity:0;transform:scale(.7);}
.slide-shell.is-active .reveal-mark{animation:popIn .9s .55s cubic-bezier(.2,1.3,.3,1) forwards;}
@keyframes popIn{to{opacity:1;transform:none;}}
.reveal-lockup{transform:scale(4.2);transform-origin:center;filter:drop-shadow(0 0 70px rgba(223,255,61,.30));}
.reveal-tag{margin-top:120px;}
.reveal-box{width:150px;height:150px;border-radius:34px;background:var(--lime);display:grid;place-items:center;box-shadow:0 0 90px rgba(223,255,61,.35);}
.reveal-name{font-family:var(--font-display);font-size:104px;font-weight:600;letter-spacing:-.035em;line-height:1;}
.reveal-row{display:grid;grid-template-columns:repeat(3,1fr);gap:24px;width:100%;margin-top:auto;}
.reveal-card{background:var(--ink-2);border:1px solid var(--line-3);border-radius:20px;padding:26px;display:flex;flex-direction:column;gap:10px;text-align:left;}
.reveal-t{font-family:var(--font-display);font-size:34px;font-weight:600;letter-spacing:-.02em;}
.gauge{position:relative;margin-top:6px;}
.gauge svg{width:100%;height:210px;}
.gauge-track{fill:none;stroke:rgba(255,255,255,.10);stroke-width:14;stroke-linecap:round;}
.gauge-fill{fill:none;stroke:var(--lime);stroke-width:14;stroke-linecap:round;stroke-dasharray:267;stroke-dashoffset:267;}
.slide-shell.is-active .gauge-fill{animation:gfill 1.4s 1.9s ease forwards;}
@keyframes gfill{to{stroke-dashoffset:16;}}
.gauge-v{position:absolute;left:0;right:0;bottom:14px;text-align:center;font-family:var(--font-display);font-size:86px;font-weight:600;color:var(--lime);letter-spacing:-.03em;}
.risk-terms{display:flex;gap:14px;}
.risk-terms span{padding:10px 18px;border-radius:999px;border:1px solid var(--line);font-family:var(--font-mono);font-size:14px;letter-spacing:.1em;text-transform:uppercase;}
.risk-terms .dimmed{color:var(--dim-2);border-color:var(--line-3);}

/* solution collapse */
.fuse{flex:1;display:grid;place-items:center;position:relative;margin-top:20px;min-height:0;}
.fuse-old{position:absolute;display:flex;gap:26px;}
.fuse-tile{width:104px;height:104px;border-radius:22px;background:var(--ink-2);border:1px solid var(--line-3);display:grid;place-items:center;}
.fuse-tile img{width:52px;height:52px;border-radius:12px;background:#fff;padding:6px;object-fit:contain;}
.slide-shell.is-active .fuse-tile{animation:suck 1.1s 1s cubic-bezier(.6,.05,.3,1) forwards;}
@keyframes suck{to{transform:translate(0,0) scale(.15);opacity:0;}}
.fuse-new{display:flex;flex-direction:column;align-items:center;gap:14px;text-align:center;}
.fuse-n{font-family:var(--font-display);font-size:96px;font-weight:600;letter-spacing:-.03em;}

/* one click */
.click-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:26px;margin:auto 0;}
.click-card{position:relative;width:100%;text-align:left;background:var(--ink-2);border:1px solid var(--line-3);border-radius:22px;padding:34px;color:var(--cream);cursor:pointer;min-height:330px;display:flex;flex-direction:column;gap:14px;transition:border-color .25s ease,transform .25s ease,background-color .25s ease;font-family:var(--font-body);}
.click-card:hover{transform:translateY(-8px);border-color:var(--line);}
.click-card.is-on{background:var(--lime-soft);border-color:var(--lime);}
.click-t{font-family:var(--font-display);font-size:46px;font-weight:600;letter-spacing:-.02em;}
.click-r{margin-top:auto;font-size:19px;color:var(--lime);opacity:0;transform:translateY(10px);transition:opacity .4s ease,transform .4s ease;}
.click-card.is-on .click-r{opacity:1;transform:none;}
.click-pulse{position:absolute;inset:-1px;border-radius:22px;border:1px solid var(--lime);opacity:0;}
.click-card.is-on .click-pulse{animation:ring .7s ease forwards;}
@keyframes ring{0%{opacity:.9;transform:scale(1);}100%{opacity:0;transform:scale(1.06);}}

/* rail */
.rail{position:relative;display:grid;grid-template-columns:repeat(4,1fr);gap:24px;margin:auto 0;}
.rail-line{position:absolute;left:34px;right:34px;top:26px;height:3px;background:rgba(255,255,255,.10);}
.rail-line span{display:block;height:100%;background:var(--lime);transition:width .6s ease;}
.rail-step{position:relative;z-index:2;opacity:.35;transition:opacity .4s ease,transform .4s ease;}
.rail-step.is-on{opacity:1;transform:translateY(-6px);}
.rail-dot{width:56px;height:56px;border-radius:50%;background:var(--ink);border:2px solid rgba(255,255,255,.18);display:grid;place-items:center;font-family:var(--font-display);font-size:26px;transition:all .35s ease;}
.rail-step.is-on .rail-dot{background:var(--lime);color:var(--ink);border-color:var(--lime);}
.rail-t{font-family:var(--font-display);font-size:58px;font-weight:600;letter-spacing:-.025em;margin-top:24px;}
.rail-d{font-size:21px;color:var(--dim);margin-top:10px;max-width:330px;}

/* vision */
.vision-head{display:flex;flex-direction:column;gap:14px;}
.vision{position:relative;flex:1;margin-top:12px;min-height:0;display:grid;place-items:center;}
.vision-svg{position:absolute;inset:0;width:100%;height:100%;}
.vision-line{stroke:rgba(223,255,61,.35);stroke-width:1.5;stroke-dasharray:400;stroke-dashoffset:400;}
.slide-shell.is-active .vision-line{animation:draw 1s ease forwards;animation-delay:calc(.35s + var(--i)*.15s);}
.vision-hub{width:330px;height:330px;border-radius:50%;border:1.5px solid var(--lime);background:radial-gradient(closest-side,rgba(50,72,40,.98),var(--ink) 92%);display:grid;place-items:center;gap:8px;text-align:center;z-index:2;}
.vision-hub-t{font-family:var(--font-display);font-size:38px;font-weight:600;line-height:1.1;letter-spacing:-.02em;}
.vision-sat{position:absolute;display:flex;align-items:center;gap:14px;background:var(--ink-2);border:1px solid var(--line-3);border-radius:999px;padding:20px 30px;font-family:var(--font-display);font-size:28px;font-weight:600;letter-spacing:-.01em;}
.vision-sat.tl{left:2%;top:6%;} .vision-sat.tr{right:2%;top:6%;}
.vision-sat.bl{left:2%;bottom:6%;} .vision-sat.br{right:2%;bottom:6%;}

/* three tiles */
.three{display:grid;grid-template-columns:repeat(3,1fr);gap:30px;margin:auto 0;}
.tile{background:var(--ink-2);border:1px solid var(--line-3);border-radius:22px;padding:38px;display:flex;flex-direction:column;gap:16px;min-height:330px;}
.tile-t{font-family:var(--font-display);font-size:48px;font-weight:600;letter-spacing:-.025em;}
.tile-d{font-size:22px;color:var(--dim);}

/* market */
.market{display:grid;grid-template-columns:1fr 460px;gap:56px;margin-top:40px;flex:1;min-height:0;align-items:center;}
.market-nums{display:flex;flex-direction:column;gap:34px;}
.market-v{font-family:var(--font-display);font-size:104px;font-weight:600;line-height:.9;letter-spacing:-.04em;color:var(--lime);}
.digi{background:var(--ink-2);border:1px solid var(--line-3);border-radius:24px;padding:34px;display:flex;flex-direction:column;gap:16px;align-items:center;text-align:center;}
.donut{position:relative;width:300px;}
.donut svg{width:100%;transform:rotate(-90deg);}
.donut-track{fill:none;stroke:rgba(255,255,255,.08);stroke-width:18;}
.donut-fill{fill:none;stroke:var(--lime);stroke-width:18;stroke-linecap:round;stroke-dasharray:515;stroke-dashoffset:515;}
.slide-shell.is-active .donut-fill{animation:dfill 1.4s .4s ease forwards;}
@keyframes dfill{to{stroke-dashoffset:479;}}
.donut-v{position:absolute;inset:0;display:grid;place-items:center;font-family:var(--font-display);font-size:76px;font-weight:600;color:var(--lime);}

/* customer #1 */
.cust{display:grid;grid-template-columns:1.15fr 1fr;gap:36px;margin-top:44px;flex:1;min-height:0;}
.cust-card{background:var(--ink-2);border:1px solid var(--line);border-radius:24px;padding:38px;display:flex;flex-direction:column;gap:36px;}
.cust-logo{display:flex;align-items:center;gap:20px;}
.cust-logo img{width:76px;height:76px;border-radius:18px;background:#fff;padding:8px;object-fit:contain;}
.cust-mark{width:76px;height:76px;border-radius:18px;background:var(--lime);color:var(--ink);display:grid;place-items:center;font-family:var(--font-display);font-weight:700;font-size:32px;letter-spacing:-.02em;}
.cust-n{font-family:var(--font-display);font-size:50px;font-weight:600;letter-spacing:-.025em;}
.cust-stats{display:grid;grid-template-columns:repeat(3,1fr);gap:22px;margin-top:auto;}
.cust-v{font-family:var(--font-display);font-size:64px;font-weight:600;letter-spacing:-.03em;line-height:1;}
.pipe{background:var(--lime-soft);border:1px solid var(--line);border-radius:24px;padding:38px;display:flex;flex-direction:column;gap:24px;}
.pipe-grid{display:grid;grid-template-columns:repeat(5,1fr);gap:16px;}
.pipe-av{aspect-ratio:1;border-radius:16px;background:rgba(255,255,255,.06);display:grid;place-items:center;color:var(--lime);opacity:0;transform:scale(.7);}
.slide-shell.is-active .pipe-av{animation:pop .45s forwards;animation-delay:calc(.8s + var(--i)*.11s);}
@keyframes pop{to{opacity:1;transform:none;}}
.pipe-v{margin-top:auto;font-family:var(--font-display);font-size:72px;font-weight:600;color:var(--lime);letter-spacing:-.03em;}
.pipe-l{font-size:26px;color:var(--dim);letter-spacing:0;}

/* business */
.rev-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:18px;margin-top:30px;}
.rev-card{background:var(--ink-2);border:1px solid var(--line-3);border-radius:18px;padding:22px;display:flex;flex-direction:column;gap:8px;}
.rev-t{font-family:var(--font-display);font-size:30px;font-weight:600;letter-spacing:-.015em;}
.align-row{display:grid;grid-template-columns:1.1fr 1fr;gap:22px;margin-top:auto;padding-top:26px;}
.align-card{background:var(--ink-2);border:1px solid var(--line-3);border-radius:20px;padding:26px 28px;display:flex;flex-direction:column;gap:16px;}
.bars{display:flex;align-items:flex-end;gap:26px;height:150px;}
.bar-pair{display:flex;align-items:flex-end;gap:6px;height:100%;flex:1;}
.bar{flex:1;border-radius:6px 6px 0 0;height:0;}
.bar.vol{background:rgba(223,255,61,.28);}
.bar.rev{background:var(--lime);}
.slide-shell.is-active .bar{animation:barUp .9s cubic-bezier(.2,.9,.2,1) forwards;animation-delay:calc(1s + var(--i)*.13s);}
@keyframes barUp{to{height:var(--h);}}
.bars-key{display:flex;gap:26px;}
.bars-key span{display:inline-flex;align-items:center;gap:8px;}
.k{width:12px;height:12px;border-radius:4px;display:inline-block;}
.k.vol{background:rgba(223,255,61,.28);} .k.rev{background:var(--lime);}
.seat-row{display:flex;gap:12px;align-items:center;}
.seat{width:60px;height:60px;border-radius:16px;background:var(--lime-soft);border:1px solid var(--line);color:var(--lime);display:grid;place-items:center;opacity:0;transform:scale(.7);}
.slide-shell.is-active .seat{animation:pop .4s forwards;animation-delay:calc(1.3s + var(--i)*.12s);}
.seat.plus{font-family:var(--font-display);font-size:30px;background:none;border-style:dashed;opacity:1;transform:none;animation:none;}
.seat-note{font-size:20px;color:var(--dim);}

/* lanes (banking at the core) */
.lanes{display:flex;flex-direction:column;gap:32px;margin:auto 0;}
.lane{border:1px solid var(--line-3);border-radius:22px;padding:28px 32px;display:flex;flex-direction:column;gap:20px;}
.lane.good{border-color:var(--line);background:var(--lime-soft);}
.lane-row{display:flex;align-items:center;gap:14px;flex-wrap:nowrap;}
.lane-chip{display:flex;align-items:center;gap:10px;padding:16px 20px;border-radius:14px;background:rgba(255,255,255,.05);color:var(--dim);font-size:19px;white-space:nowrap;}
.lane-chip.on{background:rgba(223,255,61,.10);border:1px solid var(--line);color:var(--cream);}
.lane-gap{width:70px;text-align:center;font-family:var(--font-display);font-size:38px;color:#ff9c92;border-top:2px dashed rgba(255,107,94,.5);}
.lane-arrow{width:70px;text-align:center;font-family:var(--font-mono);font-size:30px;color:var(--lime);}
.lane-end{margin-left:auto;display:flex;align-items:center;gap:12px;padding:18px 26px;border-radius:16px;border:1px dashed rgba(255,255,255,.2);font-family:var(--font-display);font-size:26px;font-weight:600;color:var(--dim);white-space:nowrap;}
.lane-end.lime-end{border:none;background:var(--lime);color:var(--ink);}
.lane-note{font-size:21px;}
.bad-note{color:#ff9c92;} .good-note{color:var(--lime);}

/* who we serve */
.scale-wrap{margin-top:44px;display:flex;flex-direction:column;gap:12px;}
.scale-bar{height:16px;border-radius:999px;background:rgba(255,255,255,.07);position:relative;overflow:hidden;}
.scale-band{position:absolute;left:12%;top:0;bottom:0;width:0;background:var(--lime);border-radius:999px;}
.slide-shell.is-active .scale-band{animation:band 1.1s .4s cubic-bezier(.2,.9,.2,1) forwards;}
@keyframes band{to{width:58%;}}
.scale-marks{display:flex;justify-content:space-between;}
.scale-legend{margin-top:6px;}
.cat-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:18px;margin-top:70px;}
.cat-tile{display:flex;align-items:center;gap:14px;background:var(--ink-2);border:1px solid var(--line-3);border-radius:16px;padding:24px 22px;font-family:var(--font-display);font-size:25px;font-weight:600;letter-spacing:-.01em;}

/* what's next */
.next-row{position:relative;display:grid;grid-template-columns:repeat(3,1fr);gap:28px;margin:52px 0 auto;}
.next-card{background:var(--ink-2);border:1px solid var(--line-3);border-radius:22px;padding:40px;display:flex;flex-direction:column;gap:18px;min-height:440px;}
.next-step{font-family:var(--font-mono);color:var(--lime);}
.next-line{position:absolute;left:0;right:0;top:-26px;height:2px;background:rgba(255,255,255,.08);}
.next-line span{display:block;height:100%;background:var(--lime);width:0;}
.slide-shell.is-active .next-line span{animation:band2 1.6s .3s ease forwards;}
@keyframes band2{to{width:100%;}}

/* vision (ownership) */
.own-row{display:grid;grid-template-columns:repeat(4,1fr);gap:24px;margin-top:52px;}
.own-card{background:var(--ink-2);border:1px solid var(--line-3);border-radius:22px;padding:36px;display:flex;flex-direction:column;gap:12px;align-items:flex-start;min-height:340px;}
.own-card img{width:58px;height:58px;border-radius:14px;background:#fff;padding:7px;object-fit:contain;}
.own-card.is-verqo{background:var(--lime-soft);border-color:var(--lime);}
.own-lock{transform:scale(1.15);transform-origin:left center;margin-bottom:8px;}
.own-n{font-family:var(--font-display);font-size:42px;font-weight:600;letter-spacing:-.025em;margin-top:auto;}

/* closing */
.close-body{display:flex;flex-direction:column;height:100%;}
.close-lock{transform:scale(1.9);transform-origin:left center;margin:10px 0 60px;}
.close-h{font-family:var(--font-display);font-weight:600;font-size:104px;line-height:1.02;letter-spacing:-.035em;}
.close-thanks{font-family:var(--font-display);font-size:56px;color:var(--dim);letter-spacing:-.02em;margin-top:40px;}
.close-foot{margin-top:auto;display:flex;justify-content:space-between;align-items:center;}
.mission-line{font-size:30px;color:var(--dim);max-width:1180px;margin-top:30px;}

/* quadrant */
.quad{position:relative;flex:1;margin-top:22px;display:grid;grid-template-columns:46px 1fr;grid-template-rows:1fr 46px;min-height:0;}
.quad-y{grid-column:1;grid-row:1;writing-mode:vertical-rl;transform:rotate(180deg);place-self:center;}
.quad-x{grid-column:2;grid-row:2;place-self:center;}
.quad-plot{grid-column:2;grid-row:1;position:relative;}
.quad-axis-y{position:absolute;left:0;top:0;bottom:0;width:2px;background:linear-gradient(180deg,var(--line),rgba(255,255,255,.10));}
.quad-axis-x{position:absolute;left:0;right:0;bottom:0;height:2px;background:linear-gradient(90deg,rgba(255,255,255,.10),var(--line));}
.quad-pt{position:absolute;transform:translate(-50%,50%);display:flex;align-items:center;gap:10px;background:rgba(10,31,28,.9);border:1px solid var(--line-3);border-radius:999px;padding:10px 18px 10px 12px;}
.quad-pt img{width:30px;height:30px;border-radius:8px;background:#fff;padding:3px;object-fit:contain;}
.quad-pt span{font-family:var(--font-display);font-size:22px;font-weight:600;}
.quad-pt.is-verqo{background:var(--lime);border-color:var(--lime);color:var(--ink);padding:14px 26px 14px 16px;}
.quad-pt.is-verqo span{font-size:30px;}
.quad-pt.is-verqo .brand-mark{border-color:var(--ink);color:var(--ink);}
.slide-shell.is-active .quad-pt.is-verqo{animation:rise .7s forwards,pulse 3s 1.9s ease-in-out infinite;}

/* proceeds */
.proceeds{display:grid;grid-template-columns:420px 220px 1fr;align-items:center;gap:24px;flex:1;margin-top:20px;min-height:0;}
.pro-circle{width:400px;height:400px;border-radius:50%;border:2px solid var(--lime);display:grid;place-items:center;gap:8px;background:radial-gradient(closest-side,rgba(223,255,61,.10),transparent);}
.pro-amt{font-family:var(--font-display);font-size:132px;font-weight:600;letter-spacing:-.045em;color:var(--lime);line-height:1;}
.pro-svg{width:100%;height:400px;}
.pro-path{fill:none;stroke:rgba(223,255,61,.45);stroke-width:2;stroke-dasharray:300;stroke-dashoffset:300;marker-end:none;}
.slide-shell.is-active .pro-path{animation:draw 1s ease forwards;animation-delay:calc(.5s + var(--i)*.25s);}
.pro-col{display:flex;flex-direction:column;gap:26px;}
.pro-card{display:flex;gap:22px;align-items:flex-start;background:var(--ink-2);border:1px solid var(--line-3);border-radius:22px;padding:32px 34px;}
.pro-v{font-family:var(--font-display);font-size:66px;font-weight:600;letter-spacing:-.03em;line-height:1;color:var(--lime);}
.pro-t{font-family:var(--font-display);font-size:34px;font-weight:600;letter-spacing:-.015em;margin:8px 0 6px;}

/* mission */
.mission{display:flex;flex-direction:column;height:100%;}
.mission-h{font-family:var(--font-display);font-weight:600;font-size:132px;line-height:1.02;letter-spacing:-.035em;margin-top:36px;}
.mission-row{display:grid;grid-template-columns:repeat(3,1fr);gap:26px;margin-top:auto;}
.mission-card{background:var(--ink-2);border:1px solid var(--line-3);border-radius:22px;padding:30px;display:flex;flex-direction:column;gap:12px;}
.mission-card.lime-card{background:var(--lime-soft);border-color:var(--line);}
.mission-v{font-family:var(--font-display);font-size:46px;font-weight:600;letter-spacing:-.025em;}

/* chrome */
.deck-chrome{position:fixed;bottom:20px;left:50%;transform:translateX(-50%);z-index:50;display:flex;flex-direction:column;align-items:center;gap:10px;pointer-events:none;opacity:0;transition:opacity .35s ease;}
.deck-chrome.is-visible{opacity:1;}
.chrome-progress{width:260px;height:3px;background:rgba(255,255,255,.08);border-radius:999px;overflow:hidden;}
.chrome-bar{height:100%;background:var(--lime);transition:width .3s ease;}
.chrome-row{pointer-events:auto;display:flex;align-items:center;gap:8px;background:rgba(11,25,20,.85);backdrop-filter:blur(8px);border:1px solid var(--line-3);border-radius:999px;padding:6px;}
.chrome-btn{width:40px;height:40px;border-radius:999px;background:transparent;border:none;color:var(--cream);font-family:var(--font-mono);font-size:16px;cursor:pointer;display:grid;place-items:center;}
.chrome-btn:hover:not(:disabled){background:rgba(223,255,61,.10);}
.chrome-btn:disabled{color:var(--dim-2);cursor:not-allowed;}
.chrome-btn.wide{width:auto;padding:0 16px;font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:var(--lime);}
.chrome-idx{padding:0 10px;font-size:12px;}
.print-hint{pointer-events:none;background:var(--lime);color:var(--ink);border-radius:12px;padding:12px 18px;font-size:14px;font-family:var(--font-body);max-width:520px;text-align:center;}

@media print{
  @page{size:20in 11.25in;margin:0;}
  html,body{margin:0!important;padding:0!important;background:#0b1914!important;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
  .deck-outer{position:static!important;overflow:visible!important;background:var(--ink)!important;}
  .deck-canvas{position:static!important;transform:none!important;width:1920px!important;height:auto!important;}
  .slide-shell{position:relative!important;opacity:1!important;pointer-events:auto!important;width:1920px!important;height:1080px!important;overflow:hidden!important;page-break-after:always;break-after:page;}
  .slide-shell:last-child{page-break-after:auto;break-after:auto;}
  .slide{position:absolute!important;top:0;left:0;width:1920px!important;height:1080px!important;transform:none!important;}
  .deck-chrome{display:none!important;}
  .r,.mail,.pipe-av,.seat,.reveal-mark,.tangle-hub,.quad-pt,.float{opacity:1!important;transform:none!important;animation:none!important;}
  .quad-pt{transform:translate(-50%,50%)!important;}
  .glow{animation:none!important;}
  .fuse-old{display:none!important;}
  .bar{height:var(--h)!important;animation:none!important;}
  .dso-fill{width:92%!important;animation:none!important;}
  .scale-band{width:58%!important;animation:none!important;}
  .next-line span,.chrome-bar{width:100%!important;animation:none!important;}
  .sweep::after{width:100%!important;animation:none!important;}
  .map-path,.vision-line,.pro-path,.flow-path,.tangle-path{stroke-dashoffset:0!important;animation:none!important;}
  .gauge-fill{stroke-dashoffset:16!important;animation:none!important;}
  .donut-fill{stroke-dashoffset:479!important;animation:none!important;}
  .flip-inner{transform:rotateY(180deg)!important;animation:none!important;}
  .click-r{opacity:1!important;transform:none!important;}
  .burst-ring{opacity:.35!important;animation:none!important;}
}
`;
